import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { location } from '@cwos/api-client';
import { usePermissions } from '../permissions.js';
import {
  PENDING_CHOICE_REQUIRED,
  PENDING_REASON_REQUIRED,
  SCHEDULE_AT_REQUIRED,
  formatScheduleInstant,
  type ScheduleStatement,
} from './copy.js';

/**
 * One draft of the ORGANIZATION's location policy, shared by the three hub
 * pages. Each page edits its own slice; saving always sends the WHOLE body so
 * the wire shape is explicit and the org row never half-materialises — when
 * nothing is stored yet (the product default), the first save stores exactly
 * what the admin was looking at.
 *
 * Numeric fields are held as STRINGS while being edited (so a half-typed
 * value is not coerced under the cursor) and validated against the REAL
 * schema bounds mirrored in `location.POLICY_BOUNDS` — the same numbers
 * `upsertPolicySchema` enforces server-side.
 */

/**
 * WHEN A SAVE BEGINS TO GOVERN — the operator's own choice, kept as TWO fields
 * because it is two questions.
 *
 *   'immediate'  no schedule stated. The wire carries `scheduledEffectiveAt:
 *                null`, which CLEARS any schedule stored on the row. Omission
 *                would mean "leave whatever is there alone" and this form always
 *                sends the whole body, so omission here would silently defer a
 *                save the operator believes is immediate.
 *   'at'         an instant stated. `scheduledLocal` is the raw
 *                `datetime-local` value — local wall time, no zone — held as a
 *                string while it is being typed, exactly as the numeric fields
 *                are, so a half-entered date is never coerced under the cursor.
 *
 * THE TWO ARE NOT COLLAPSIBLE. `unscheduled` and `stated`-at-this-minute govern
 * identically today and are different evidence forever after; the founder's
 * ruling turns on the difference ("a reduction may apply immediately UNLESS
 * EXPLICITLY SCHEDULED FOR LATER"), the wire keeps them apart, the version
 * series records which one happened, and this is the only place a human picks.
 */
export type ScheduleMode = 'immediate' | 'at';

/**
 * WHAT THIS SAVE DOES TO ONE REVISION ALREADY WAITING.
 *
 * `relationship: null` is THE UNANSWERED STATE and it is the initial one. It is
 * not `'keep'` and it is not any of the four: the founder's ruling of
 * 2026-09-08 says the operator must be shown the pending revision and
 * explicitly choose, so the form starts with no choice made, refuses to save
 * until one is, and sends what was chosen. A pre-selected radio would be this
 * screen answering on the operator's behalf, which is the omission the ruling
 * forbids wearing a control.
 */
export interface PendingChoice {
  relationship: location.PendingRelationship | null;
  /** Raw text, held as a string while typed. Required for supersede and cancel. */
  reason: string;
}

export interface PolicyDraft {
  scheduleMode: ScheduleMode;
  /** Raw `datetime-local` text. Meaningful only when scheduleMode is 'at'. */
  scheduledLocal: string;
  /**
   * One entry per revision waiting on this scope, keyed by its version id.
   *
   * KEYED BY ID AND NOT BY POSITION, because the set can change under the form:
   * a revision arrives while the operator is typing and is no longer pending,
   * and an index would then attach their choice to a different revision.
   */
  pendingChoices: Record<string, PendingChoice>;
  mode: location.PolicyMode;
  clockInLocationRequired: boolean;
  clockOutLocationRequired: boolean;
  breadcrumbEnabled: boolean;
  breadcrumbIntervalSeconds: string;
  minAccuracyMeters: number;
  allowAttestedOverride: boolean;
  breadcrumbRetentionDays: string;
  exceptionReasonRequired: boolean;
  exceptionApprovalRequired: boolean;
}

export type PolicyDraftErrors = Partial<
  Record<'breadcrumbIntervalSeconds' | 'breadcrumbRetentionDays' | 'scheduledLocal', string>
> & {
  /**
   * Per-revision refusals, keyed by version id: an unanswered choice, or a
   * withdrawal with no reason. Separate from the flat fields above because
   * there is one of these PER WAITING REVISION and a single string could only
   * ever describe the first.
   */
  pendingChoices?: Record<string, string>;
};

/**
 * A FRESH DRAFT IS ALWAYS UNSCHEDULED, and that is a decision rather than a
 * default.
 *
 * `GET /location/policy/effective` answers with the version IN FORCE, so the
 * values in this draft are the ones governing today. A change the operator
 * scheduled earlier is a DIFFERENT version with DIFFERENT values, and seeding
 * its instant here would attach that schedule to values it was never saved
 * with — the form would then claim the rules on screen are the ones scheduled
 * for that date. The pending change is surfaced separately, as itself
 * (`PolicyForm.pending`), so "what I am about to do" and "what is already
 * coming" never merge into one control.
 */
function draftOf(policy: location.EffectivePolicy): PolicyDraft {
  return {
    scheduleMode: 'immediate',
    scheduledLocal: '',
    // EMPTY, ALWAYS. The waiting revisions are read separately and the choices
    // are filled in by the human; seeding a relationship here — even `keep` —
    // would be this form deciding, which is the exact act the ruling of
    // 2026-09-08 forbids.
    pendingChoices: {},
    mode: policy.mode,
    clockInLocationRequired: policy.clockInLocationRequired,
    clockOutLocationRequired: policy.clockOutLocationRequired,
    breadcrumbEnabled: policy.breadcrumbEnabled,
    breadcrumbIntervalSeconds: String(policy.breadcrumbIntervalSeconds),
    minAccuracyMeters: policy.minAccuracyMeters,
    allowAttestedOverride: policy.allowAttestedOverride,
    breadcrumbRetentionDays: String(policy.breadcrumbRetentionDays),
    exceptionReasonRequired: policy.exceptionReasonRequired,
    exceptionApprovalRequired: policy.exceptionApprovalRequired,
  };
}

/** An integer within [min, max], else null. */
function boundedInt(raw: string, bounds: { min: number; max: number }): number | null {
  const trimmed = raw.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return n >= bounds.min && n <= bounds.max ? n : null;
}

/**
 * The schema's own refusals, in the page's words.
 *
 * `pendingRevisions` is a SECOND ARGUMENT and not a field on the draft, because
 * it is not the operator's input: it is the state of the scope, read from the
 * server, and the draft has to be judged against it. Passing `[]` is a claim
 * that nothing is waiting; the caller that does not know must not call this
 * yet, which is why `PolicyForm.pendingRevisions` is `undefined` until the read
 * lands and the save button is disabled while it is.
 */
export function validateDraft(
  draft: PolicyDraft,
  pendingRevisions: readonly location.PendingPolicyChange[] = [],
): PolicyDraftErrors {
  const errors: PolicyDraftErrors = {};
  // ONE CHOICE PER WAITING REVISION, AND A REASON FOR THE TWO THAT WITHDRAW.
  // The server refuses the same two things; this is the same refusal a moment
  // earlier and in the operator's words, never a substitute for it — the
  // decision is made against the live series inside the saving transaction,
  // which this browser cannot see.
  const perRevision: Record<string, string> = {};
  for (const revision of pendingRevisions) {
    const choice = draft.pendingChoices[revision.versionId];
    if (!choice || choice.relationship === null) {
      perRevision[revision.versionId] = PENDING_CHOICE_REQUIRED;
      continue;
    }
    const withdraws = choice.relationship === 'supersede' || choice.relationship === 'cancel';
    if (withdraws && choice.reason.trim().length === 0) {
      perRevision[revision.versionId] = PENDING_REASON_REQUIRED;
    }
  }
  if (Object.keys(perRevision).length > 0) errors.pendingChoices = perRevision;
  if (
    boundedInt(draft.breadcrumbIntervalSeconds, location.POLICY_BOUNDS.breadcrumbIntervalSeconds) ==
    null
  ) {
    errors.breadcrumbIntervalSeconds =
      'Between 30 seconds and 24 hours (86,400 seconds), in whole seconds.';
  }
  if (
    boundedInt(draft.breadcrumbRetentionDays, location.POLICY_BOUNDS.breadcrumbRetentionDays) ==
    null
  ) {
    errors.breadcrumbRetentionDays = 'Between 1 and 3,650 days, in whole days.';
  }
  // A schedule the browser cannot turn into an instant is not a schedule. An
  // empty picker under "At a time I choose" is the common case and it is an
  // ERROR rather than a silent fall-back to immediate: falling back would send
  // `null`, clearing a schedule for an operator whose visible choice says they
  // are setting one.
  if (draft.scheduleMode === 'at' && scheduledInstantOf(draft) === null) {
    errors.scheduledLocal = SCHEDULE_AT_REQUIRED;
  }
  return errors;
}

/**
 * The `datetime-local` string as a real instant, or null when it is not one.
 *
 * `new Date('2026-12-01T09:00')` is parsed as LOCAL wall time by every browser
 * this app targets, which is what the control means and what the zone sentence
 * beside it tells the operator. `toISOString()` then names the same moment
 * unambiguously, which is what the server stores — so the round trip loses
 * nothing and invents no zone.
 */
export function scheduledInstantOf(draft: PolicyDraft): Date | null {
  if (draft.scheduleMode !== 'at') return null;
  const raw = draft.scheduledLocal.trim();
  if (raw.length === 0) return null;
  const at = new Date(raw);
  return Number.isFinite(at.getTime()) ? at : null;
}

/**
 * WHICH SENTENCE A CONSEQUENCE NOTE SHOULD BE IN, decided once.
 *
 * `null` means THE SCREEN CANNOT SAY YET: the operator has opened the picker and
 * not finished choosing, so neither "as soon as you save" nor "beginning at X"
 * is true. A note is withheld rather than defaulted — printing the immediate
 * sentence beside a radio that reads "At a time I choose" would put two
 * contradictory claims on one screen, and printing a guessed instant would be
 * worse. The save bar is already refusing the draft for the same reason.
 */
export function scheduleStatementOf(draft: PolicyDraft): ScheduleStatement | null {
  if (draft.scheduleMode === 'immediate') return { kind: 'immediate' };
  const at = scheduledInstantOf(draft);
  return at === null ? null : { kind: 'at', when: formatScheduleInstant(at.toISOString()) };
}

/**
 * The exact `PUT /location/policy` body for a valid draft — every field
 * explicit. Field-for-field this is the wire contract the component tests pin.
 *
 * `programId` IS WHICH SCOPE THIS BODY WRITES: `null` is the organization row
 * and a string is that program's override row — the same two-valued scope the
 * server keys the whole domain on (`program_id is not distinct from $1`).
 *
 * REQUIRED, WITH NO DEFAULT. A defaulted `null` would let a program editor that
 * forgot to pass its id silently rewrite the ORGANIZATION's policy: a save that
 * reaches every worker in the tenant instead of one program's, from a screen
 * headed with the program's name. The compiler asks instead.
 *
 * THIS IS THE ONLY BODY BUILDER IN THE CONSOLE, and that is a property rather
 * than a coincidence. There used to be a second one for the per-program
 * override row, which is exactly how the program scope ended up able to send a
 * mode and nothing else; `location-schedule-reach.test.ts` counts the builders
 * and reads the count back out of the ledger, so a second one cannot reappear
 * quietly.
 */
export function upsertBodyOf(
  draft: PolicyDraft,
  pendingRevisions: readonly location.PendingPolicyChange[],
  programId: string | null,
): location.PolicyUpsert {
  const instant = scheduledInstantOf(draft);
  return {
    // OMITTED FOR THE ORGANIZATION, never `programId: null`. The wire type has
    // no null branch (`programId?: string`), and the org body every existing
    // caller sends is the one without the key at all.
    ...(programId === null ? {} : { programId }),
    // EVERY WAITING REVISION, EXACTLY ONCE — built from the SERVER'S list and
    // not from the draft's own keys. A choice left over for a revision that has
    // since arrived would be a stale statement the server refuses, and a
    // revision the draft has no key for would be one this save says nothing
    // about, which the server also refuses. Driving the array off the server's
    // set makes both of those impossible to send by accident, and the
    // validation above makes them impossible to send on purpose.
    //
    // OMITTED WHEN NOTHING IS WAITING. An empty array and an absent field are
    // the same act to the server when the scope has nothing pending, and the
    // absent one keeps every existing caller's body unchanged.
    ...(pendingRevisions.length > 0
      ? {
          pendingRevisions: pendingRevisions.map((revision) => {
            const choice = draft.pendingChoices[revision.versionId];
            if (!choice || choice.relationship === null) {
              // UNREACHABLE BY CONSTRUCTION and LOUD rather than defaulted.
              // `validateDraft` refuses this draft and `save()` will not call
              // here, so reaching it means the two have drifted apart. The
              // tempting line is `?? 'keep'`; it would make the drift invisible
              // and would be this function silently choosing on an operator's
              // behalf — the precise omission the ruling of 2026-09-08 forbids,
              // wearing a fallback.
              throw new Error(
                `no relationship chosen for scheduled revision ${revision.versionId}: ` +
                  'a body cannot be built for a draft the form has not finished',
              );
            }
            return {
              versionId: revision.versionId,
              relationship: choice.relationship,
              reason: choice.reason.trim() ? choice.reason.trim() : null,
            };
          }),
        }
      : {}),
    // EXPLICIT `null`, NOT OMISSION, for an immediate save. The server reads
    // omission as "leave the stored schedule alone" and `null` as "clear it",
    // and this form always sends the whole body — so omitting the field would
    // leave a previously stored instant still deferring a save the operator was
    // told applies as soon as they press it.
    scheduledEffectiveAt: instant === null ? null : instant.toISOString(),
    mode: draft.mode,
    clockInLocationRequired: draft.clockInLocationRequired,
    clockOutLocationRequired: draft.clockOutLocationRequired,
    breadcrumbEnabled: draft.breadcrumbEnabled,
    breadcrumbIntervalSeconds: Number(draft.breadcrumbIntervalSeconds.trim()),
    minAccuracyMeters: draft.minAccuracyMeters,
    allowAttestedOverride: draft.allowAttestedOverride,
    breadcrumbRetentionDays: Number(draft.breadcrumbRetentionDays.trim()),
    exceptionReasonRequired: draft.exceptionReasonRequired,
    exceptionApprovalRequired: draft.exceptionApprovalRequired,
  };
}

export interface PolicyForm {
  /** The saved policy as the server resolved it (undefined while loading). */
  policy: location.EffectivePolicy | undefined;
  /**
   * A change ALREADY PUBLISHED for this scope whose instant has not arrived.
   *
   * `undefined` = the read has not landed. `null` = the server answered and
   * there is nothing pending. Those are different states and a screen must not
   * render the first as the second — "no change is scheduled" is a claim, and
   * an unarrived answer is not one.
   */
  pending: location.PendingPolicyChange | null | undefined;
  /**
   * EVERY revision waiting on this scope — the set a save must state a
   * relationship to, and the set this screen must render a choice for.
   *
   * `undefined` = the read has not landed. An EMPTY ARRAY is the server's claim
   * that nothing is waiting, and only that may be rendered as "nothing is
   * scheduled". The two are kept apart for the reason `pending` is: a screen
   * that showed an unarrived answer as "nothing is scheduled" would let an
   * operator save over a change they were never shown, which is the whole
   * defect.
   */
  pendingRevisions: readonly location.PendingPolicyChange[] | undefined;
  /**
   * A CHANGE COMING FROM A LAYER ABOVE — waiting somewhere this form does not
   * write, and therefore not something this save may state a relationship to.
   *
   * `pending` and `pendingRevisions` answer two different questions and at
   * PROGRAM scope they can disagree: `pendingRevisions` is what is waiting on
   * the program row, while `pending` is the next change that alters what governs
   * this program — which, for a program that does not override, is the
   * ORGANIZATION's revision. This field is exactly `pending` when it is NOT one
   * of `pendingRevisions`, and null otherwise.
   *
   * At organization scope it is ALWAYS null and that is a property rather than
   * an accident: the org row's `pending` is the earliest of its own live
   * revisions, so it is always in the list. The field is therefore quiet on
   * three of the four saving pages and load-bearing on the program editor — and
   * a fixture pins each half, because a guard that cannot be seen firing is a
   * guard nobody knows is there.
   *
   * `undefined` = the read has not landed. `null` = the server answered and
   * nothing above this scope is coming.
   */
  inheritedPending: location.PendingPolicyChange | null | undefined;
  /**
   * THE LAST SAVE'S RESULTING GOVERNING TIMELINE — what governs now, what is
   * still coming, and what that save withdrew. The server's own answer, not a
   * re-derivation: an operator who just cancelled a revision and one who just
   * kept one need different sentences and only the server knows which happened.
   */
  saveTimeline: location.GoverningTimeline | null;
  /** Chooses (or re-chooses) what this save does to ONE waiting revision. */
  choosePending: (versionId: string, patch: Partial<PendingChoice>) => void;
  /**
   * WHAT THE LAST SAVE ACTUALLY DID — the server's own schedule report, not a
   * re-derivation from the draft. It says whether the values are in force yet,
   * which floors are still in the way, and from when. null until a save
   * succeeds in this session.
   */
  saveSchedule: location.PolicySaveSchedule | null;
  loading: boolean;
  loadError: boolean;
  /**
   * The rejected read itself, so a screen can tell a REFUSAL from an outage.
   *
   * `loadError` is a boolean and a boolean cannot answer "why", so all four
   * policy pages rendered a 403 on `GET /location/policy/effective` as "We
   * couldn't load the policy just now" with a Try again button — advice that
   * can never work, since only a grant changes the answer. Kept beside
   * `loadError` rather than replacing it: the boolean also encodes "and there
   * is no cached policy to fall back on", which the error alone does not say.
   */
  loadFailure: unknown;
  refetch: () => void;
  draft: PolicyDraft | null;
  set: (patch: Partial<PolicyDraft>) => void;
  errors: PolicyDraftErrors;
  valid: boolean;
  dirty: boolean;
  saving: boolean;
  saved: boolean;
  saveError: string | null;
  save: () => void;
  cancel: () => void;
  /** `location:manage` — the write gate. Read-only renders disable controls. */
  canManage: boolean;
}

/**
 * ONE DRAFT OF ONE SCOPE'S POLICY.
 *
 * `programId` is `null` for the ORGANIZATION row and a program's id for that
 * program's override row — the same two-valued scope the server keys the whole
 * domain on (`program_id is not distinct from $1`), so there is no third state
 * to get wrong.
 *
 * REQUIRED, WITH NO DEFAULT, for the reason `upsertBodyOf` gives: a program
 * editor that omitted it would read and then overwrite the ORGANIZATION's
 * policy while displaying a program's name, which is a tracking change reaching
 * every worker in the tenant instead of one program's.
 */
export function usePolicyForm(programId: string | null): PolicyForm {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManage = has('location:manage');

  // `effectivePolicyWithSchedule`, NOT `effectivePolicy`. The narrow reader
  // answers with the version in force and drops the published-and-waiting one,
  // which is exactly the fact a form that overwrites the whole row must not be
  // missing: without it this screen re-opens showing pre-change values, no
  // schedule, and no sign that a change its own operator saved is still coming.
  //
  // THE SCOPE IS IN THE QUERY KEY. Without it the program editor and the four
  // organization pages would share one cache entry, and whichever read landed
  // last would be rendered as both — a program's values shown as the
  // organization's, and (worse) the program's `pendingRevisions` offered as the
  // set an organization save must decide about.
  const query = useQuery({
    queryKey: ['location', 'policy', 'effective', programId ?? 'org'],
    queryFn: () => location.effectivePolicyWithSchedule(programId === null ? {} : { programId }),
  });
  const policy = query.data?.policy;

  const [draft, setDraft] = useState<PolicyDraft | null>(null);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSchedule, setSaveSchedule] = useState<location.PolicySaveSchedule | null>(null);
  const [saveTimeline, setSaveTimeline] = useState<location.GoverningTimeline | null>(null);

  useEffect(() => {
    if (policy && draft === null) setDraft(draftOf(policy));
  }, [policy, draft]);

  const mutation = useMutation({
    mutationFn: (body: location.PolicyUpsert) => location.upsertPolicy(body),
    onSuccess: (next) => {
      setSaveError(null);
      setSaved(true);
      // THE SERVER'S ANSWER, KEPT. `schedule` says whether what was just saved
      // is governing anybody yet; a form that stored only `policy` would have
      // no way to tell the operator their change is waiting, which is the whole
      // point of being able to schedule one.
      setSaveSchedule(next.schedule);
      setSaveTimeline(next.timeline);
      // A FRESH DRAFT, so `pendingChoices` is emptied with it. The choices just
      // made are spent: the revisions they were about have either been withdrawn
      // or are still waiting and must be chosen about AGAIN on the next save.
      // Carrying them forward would let one decision authorise a second save the
      // operator never made it for.
      setDraft(draftOf(next.policy));
      void qc.invalidateQueries({ queryKey: ['location', 'policy'] });
    },
    onError: (err: unknown) => setSaveError(messageOf(err)),
  });

  const pendingRevisions = query.data === undefined ? undefined : query.data.pendingRevisions;
  // WHAT IS COMING FROM ABOVE, separated from what is waiting HERE — by
  // VERSION ID against the server's own list, never by comparing scopes in the
  // browser. `pending` carries no `programId`, so the only sound test of "is
  // this one of mine" is whether the set a save must decide about contains it.
  const inheritedPending =
    query.data === undefined
      ? undefined
      : query.data.pending !== null &&
          !query.data.pendingRevisions.some((r) => r.versionId === query.data!.pending!.versionId)
        ? query.data.pending
        : null;
  // VALIDATED AGAINST THE SERVER'S SET. While the read is in flight the set is
  // UNKNOWN, and an unknown set is not an empty one — validating against `[]`
  // there would call a draft valid because nothing had loaded yet, which is
  // "green on the wrong shape" in a save button. So the draft is INVALID until
  // the answer arrives.
  const errors = draft && pendingRevisions ? validateDraft(draft, pendingRevisions) : {};
  const valid =
    draft !== null && pendingRevisions !== undefined && Object.keys(errors).length === 0;
  const dirty =
    draft !== null &&
    policy !== undefined &&
    JSON.stringify(draft) !== JSON.stringify(draftOf(policy));

  return {
    policy,
    // undefined while the read is in flight or failed; null only when the
    // server said there is nothing pending.
    pending: query.data === undefined ? undefined : query.data.pending,
    pendingRevisions,
    inheritedPending,
    saveSchedule,
    saveTimeline,
    choosePending: (versionId, patch) => {
      setSaved(false);
      setSaveSchedule(null);
      setSaveTimeline(null);
      setDraft((current) =>
        current
          ? {
              ...current,
              pendingChoices: {
                ...current.pendingChoices,
                [versionId]: {
                  relationship: null,
                  reason: '',
                  ...current.pendingChoices[versionId],
                  ...patch,
                },
              },
            }
          : current,
      );
    },
    loading: query.isLoading,
    loadError: query.isError && !query.data,
    loadFailure: query.error,
    refetch: () => void query.refetch(),
    draft,
    set: (patch) => {
      setSaved(false);
      // The previous save's report describes the previous save. Once the
      // operator edits again it is no longer a description of anything on
      // screen, and leaving it up would state a governing instant for values
      // that have since changed.
      setSaveSchedule(null);
      setSaveTimeline(null);
      setDraft((current) => (current ? { ...current, ...patch } : current));
    },
    errors,
    valid,
    dirty,
    saving: mutation.isPending,
    saved,
    saveError,
    save: () => {
      if (!draft || !valid || pendingRevisions === undefined) return;
      mutation.mutate(upsertBodyOf(draft, pendingRevisions, programId));
    },
    cancel: () => {
      if (policy) setDraft(draftOf(policy));
      setSaveError(null);
      setSaved(false);
      setSaveSchedule(null);
      setSaveTimeline(null);
    },
    canManage,
  };
}

export function messageOf(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return 'That did not save.';
}
